/**
 * Detail Modal Component
 * Modal for displaying entity details with metadata, tabs, and actions
 */

import React from 'react';
import { Modal, Descriptions, Tabs, Space, Button } from 'antd';
import { CloseOutlined } from '@ant-design/icons';

/**
 * DetailModal Component
 *
 * @param {boolean} visible - Modal visibility (from useModal)
 * @param {function} onClose - Close handler
 * @param {string} title - Modal title
 * @param {string} width - Modal width (default: 800)
 * @param {array} metadata - Metadata items [{label, value, span}]
 * @param {array} tabs - Tab items [{key, label, children}]
 * @param {ReactNode} children - Content (if not using tabs)
 * @param {ReactNode} extra - Extra content in header
 * @param {array} actions - Action buttons [{label, onClick, type, icon, danger}]
 * @param {boolean} destroyOnClose - Destroy content on close (default: true)
 *
 * @example
 * const modal = useModal();
 * <DetailModal
 *   visible={modal.visible}
 *   onClose={modal.close}
 *   title="Scan Details"
 *   metadata={[
 *     { label: 'ID', value: modal.data?.id },
 *     { label: 'Status', value: <StatusTag status={modal.data?.status} /> },
 *     { label: 'Created', value: new Date(modal.data?.created_at).toLocaleString() },
 *   ]}
 *   tabs={[
 *     { key: '1', label: 'Overview', children: <div>Overview content</div> },
 *     { key: '2', label: 'Events', children: <EventTable /> },
 *   ]}
 *   actions={[
 *     { label: 'Export', onClick: handleExport, icon: <DownloadOutlined /> },
 *     { label: 'Delete', onClick: handleDelete, type: 'primary', danger: true },
 *   ]}
 * />
 */
function DetailModal({
  visible,
  onClose,
  title,
  width = 800,
  metadata,
  tabs,
  children,
  extra,
  actions,
  destroyOnClose = true,
  ...restProps
}) {
  // Build footer with actions
  const footer = actions ? (
    <Space>
      <Button onClick={onClose}>Close</Button>
      {actions.map((action, index) => (
        <Button
          key={index}
          type={action.type || 'default'}
          danger={action.danger}
          icon={action.icon}
          onClick={action.onClick}
        >
          {action.label}
        </Button>
      ))}
    </Space>
  ) : (
    <Button onClick={onClose}>Close</Button>
  );

  return (
    <Modal
      open={visible}
      onCancel={onClose}
      title={title}
      width={width}
      footer={footer}
      destroyOnClose={destroyOnClose}
      centered
      closeIcon={<CloseOutlined />}
      {...restProps}
    >
      {/* Metadata section */}
      {metadata && metadata.length > 0 && (
        <Descriptions
          column={2}
          bordered
          size="small"
          style={{ marginBottom: 24 }}
        >
          {metadata.map((item, index) => (
            <Descriptions.Item
              key={index}
              label={item.label}
              span={item.span || 1}
            >
              {item.value}
            </Descriptions.Item>
          ))}
        </Descriptions>
      )}

      {/* Tabs or children */}
      {tabs && tabs.length > 0 ? (
        <Tabs items={tabs} />
      ) : (
        children
      )}
    </Modal>
  );
}

export default DetailModal;
